<template>
  <div
    v-if="article.attributes?.item_type == 'item'"
    class="d-flex text-start border-bottom mb-2 pb-2"
    :data-article="article.id"
  >
    <div class="w-100">
      <template v-if="editor == 'art-' + article.id">
        <div class="col-12 mb-3">
          <base-form-input
            :name="editor + '-title'"
            max="255"
            :label="
              'Fügen Sie hier die Bezeichnung des Artikels hinzu z. B. ' +
              (categoryType == 'food' ? 'Tomatencremesuppe' : 'Coca-Cola')
            "
            :value="article.attributes.title"
            :errors="article.errors ? article.errors.title : null"
            @update:data="$emit('update:data', $event)"
          ></base-form-input>
        </div>
        <div class="col-12 mb-3">
          <base-textarea
            :name="editor + '-subtitle'"
            max="255"
            :label="
              'Fügen Sie hier optional eine Beschreibung des Artikels hinzu z. B. ' +
              (categoryType == 'food' ? 'mit Sauerrahm und frischem Basilikum' : 'im Glas')
            "
            :value="article.attributes.subtitle || ''"
            :errors="article.errors ? article.errors.subtitle : null"
            @update:data="$emit('update:data', $event)"
          />
        </div>
        <div class="row g-3">
          <div class="col-4">
            <div class="form-floating">
              <select
                :id="editor + '-unit'"
                class="form-select col-3"
                :value="article.attributes.unit"
                @change="$emit('update:data', $event)"
              >
                <option value="">ohne Mengeneinheit</option>
                <template v-if="['food', 'category'].includes(categoryType)">
                  <option value="Stück">Stück</option>
                  <option value="Gramm">Gramm</option>
                  <option value="Kilogramm">Kilogramm</option>
                  <option value="Zentimeter">Zentimeter</option>
                </template>
                <template v-if="['beverages', 'category'].includes(categoryType)">
                  <option value="Liter">Liter</option>
                  <option value="Zentiliter">Zentiliter</option>
                  <option value="Milliliter">Milliliter</option>
                  <option value="Flasche">Flasche</option>
                  <option value="Glas">Glas</option>
                  <option value="1/2">1/2</option>
                  <option value="1/4">1/4</option>
                  <option value="1/8">1/8</option>
                </template>
              </select>
              <label>Mengeneinheit</label>
              <template v-if="article.errors">
                <div v-for="(error, index) in article.errors.amount" :key="index" class="invalid-feedback" role="alert">
                  <strong>{{ error }}</strong>
                </div>
              </template>
            </div>
          </div>
          <div class="col-4">
            <base-form-input
              :name="editor + '-amount'"
              label="Menge"
              type="number"
              step=".01"
              :value="article.attributes.amount"
              :errors="article.errors ? article.errors.amount : null"
              @update:data="$emit('update:data', $event)"
            ></base-form-input>
          </div>
          <div class="col-4">
            <base-form-input
              :name="editor + '-price'"
              label="Preis"
              type="number"
              step=".01"
              :value="article.attributes.price"
              :errors="article.errors ? article.errors.price : null"
              @update:data="$emit('update:data', $event)"
            ></base-form-input>
          </div>
        </div>
        <div class="row g-3">
          <div :class="ingredients.length > 0 ? 'col-6' : 'col-12'">
            <div class="form-floating">
              <label style="opacity: 0.65; transform: scale(0.85) translateY(-0.5rem) translateX(0.15rem)"
                >Hinweise</label
              >
              <multiselect
                :model-value="getSelectedTagOptions(article.attributes.tags)"
                :options="tags"
                :searchable="true"
                track-by="key"
                label="value"
                :close-on-select="false"
                :show-labels="false"
                :multiple="true"
                :taggable="true"
                placeholder="bitte wählen"
                @select="$emit('update:tags', 'select', $event)"
                @remove="$emit('update:tags', 'remove', $event)"
              >
              </multiselect>
            </div>
          </div>
          <div v-if="ingredients.length > 0" class="col-6">
            <div class="form-floating">
              <label style="opacity: 0.65; transform: scale(0.85) translateY(-0.5rem) translateX(0.15rem)"
                >Allergene</label
              >
              <multiselect
                :model-value="getSelectedIngredientOptions(article.relationships.ingredients.data)"
                :options="ingredients"
                :searchable="true"
                track-by="key"
                label="value"
                :close-on-select="false"
                :show-labels="false"
                :multiple="true"
                :taggable="true"
                placeholder="bitte wählen"
                @select="$emit('update:ingredients', 'select', $event)"
                @remove="$emit('update:ingredients', 'remove', $event)"
              >
              </multiselect>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="row">
          <strong class="col-8 handle" :class="{ disabled: multicheck }">
            <svg
              height="1em"
              viewBox="0 0 1792 1792"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              class="cursor-move"
              style="margin-top: -0.25rem"
            >
              <path
                d="M1408 1088q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45zm0-384q0 26-19 45t-45 19h-896q-26 0-45-19t-19-45 19-45l448-448q19-19 45-19t45 19l448 448q19 19 19 45z"
              ></path>
            </svg>
            {{ article.attributes.title }}
            <small
              v-if="ingredients.length > 0 && article.relationships.ingredients.data.length > 0"
              class="fw-normal opacity-50"
            >
              ({{ getShortSelectedIngredients(article.relationships.ingredients.data) }})
            </small>
          </strong>
          <span class="col-1 text-end pe-0 text-nowrap">
            {{ article.attributes.amount ? new Intl.NumberFormat('de-DE').format(article.attributes.amount) : '' }}
          </span>
          <span class="col-1 ps-1 text-nowrap">
            {{ article.attributes.unit }}
          </span>
          <span class="col-2 text-end">
            {{
              article.attributes.price
                ? new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: 'EUR',
                  }).format(article.attributes.price)
                : ''
            }}
          </span>
        </div>
        <div>{{ article.attributes.subtitle }}</div>
        <div>{{ article.attributes.tags }}</div>
      </template>
    </div>
    <div>
      <div v-if="multicheck" class="checkbox ms-3 mb-3">
        <label class="d-flex gap-2 align-items-center mb-1">
          <input v-model="mltchckd" :value="'art-' + article.id" type="checkbox" class="form-check-input" />
          <div class="check fs-4"></div>
        </label>
      </div>
      <div v-else class="btn-group-vertical ms-3 mb-3" role="group" aria-label="Button group with nested dropdown">
        <button
          v-if="editor != 'art-' + article.id"
          type="button"
          class="btn border text-muted hover:text-secondary"
          @click="changeEditor('art-' + article.id)"
        >
          <i class="bi bi-pencil-fill"></i>
        </button>
        <template v-else>
          <button
            type="button"
            class="btn border text-muted hover:text-secondary"
            @click="changeEditor('art-' + article.id)"
          >
            <base-icon-save></base-icon-save>
          </button>
          <button type="button" class="btn border text-muted hover:text-primary" @click="deleteArticle(article.id)">
            <i class="bi bi-trash-fill"></i>
          </button>
        </template>
      </div>
    </div>
  </div>
  <div v-else class="d-flex justify-content-between border-bottom mb-2 pb-2">
    <strong class="handle" :class="{ disabled: multicheck }">
      <svg
        height="1em"
        viewBox="0 0 1792 1792"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        class="cursor-move"
        style="margin-top: -0.25rem"
      >
        <path
          d="M1408 1088q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45zm0-384q0 26-19 45t-45 19h-896q-26 0-45-19t-19-45 19-45l448-448q19-19 45-19t45 19l448 448q19 19 19 45z"
        ></path>
      </svg>
      <span class="ms-2" style="color: #a05a60">
        <base-icon-blankline v-if="article.attributes?.item_type == 'blankline'"></base-icon-blankline>
        <base-icon-pagebreak v-if="article.attributes?.item_type == 'pagebreak'"></base-icon-pagebreak>
      </span>
      {{ article.attributes?.item_type == 'blankline' ? 'Leerzeile' : 'Seitenumbruch' }}
    </strong>
    <div v-if="multicheck" class="checkbox ms-3">
      <label class="d-flex gap-2 align-items-center mb-1">
        <input
          type="checkbox"
          class="form-check-input"
          :checked="multichecked.indexOf('art-' + article.id) != -1"
          @change="
            updateMultichecked(
              $event.target.checked
                ? union(multichecked, ['art-' + article.id])
                : difference(multichecked, ['art-' + article.id])
            )
          "
        />
        <div class="check fs-4"></div>
      </label>
    </div>
    <button v-else type="button" class="btn border text-muted hover:text-primary" @click="deleteArticle(article.id)">
      <i class="bi bi-trash-fill"></i>
    </button>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

import { useVModel } from '@vueuse/core';

import useContent from '@/composables/useContent';

export default {
  components: {
    Multiselect,
  },
  props: {
    article: {
      type: Object,
      default: null,
    },
    categoryType: {
      type: String,
      default: null,
    },
    changeEditor: {
      type: Function,
      default: null,
    },
    deleteArticle: {
      type: Function,
      default: null,
    },
    editor: {
      type: String,
      default: '',
    },
    ingredients: {
      type: Array,
      default: null,
    },
    menuId: {
      type: Number,
      default: null,
    },
    multicheck: {
      type: Boolean,
      default: false,
    },
    multichecked: {
      type: Array,
      default: null,
    },
  },
  emits: ['update:data', 'update:tags', 'update:ingredients', 'update:multichecked'],
  setup(props, { emit }) {
    const mltchckd = useVModel(props, 'multichecked', emit);
    const { getSelectedTagOptions, tags } = useContent(props.menuId);

    // Ingredients
    const getSelectedIngredientOptions = (value) => {
      let selectedValues = value.map((item) => item.id);
      return props.ingredients.filter((item) => selectedValues.includes(item.key));
    };

    const getShortSelectedIngredients = (value) => {
      return getSelectedIngredientOptions(value)
        .map((ingredient) => ingredient.code)
        .join(',');
    };

    return {
      getSelectedIngredientOptions,
      getSelectedTagOptions,
      getShortSelectedIngredients,
      tags,
      mltchckd,
    };
  },
};
</script>

<style lang="scss" scoped></style>
